import type { AdminPagesEditorConfig } from '../config';

export type PageMetafieldsPreviewTarget = 'live' | 'beta';

export interface PageMetafieldsPreviewTargetOption {
  label: string;
  value: PageMetafieldsPreviewTarget;
}

export interface BuildPageMetafieldsPreviewUrlOptions {
  path: string;
  siteUrl: string;
  cacheBust: number;
  /**
   * Marks the URL as an admin preview so draft-only pages skip the site's
   * unknown-slug 404 guards and super-collections render unpublished drafts.
   */
  adminPreview?: boolean;
}

export function buildPageMetafieldsPreviewUrl(options: BuildPageMetafieldsPreviewUrlOptions): string {
  const { path, siteUrl, cacheBust, adminPreview = false } = options;
  try {
    const url = new URL(path, siteUrl);
    if (adminPreview) url.searchParams.set('admin_preview', '1');
    url.searchParams.set('cache_bust', String(cacheBust));
    return url.toString();
  } catch {
    const separator = path.includes('?') ? '&' : '?';
    return `${siteUrl.replace(/\/$/, '')}/${path.replace(/^\//, '')}${separator}cache_bust=${cacheBust}`;
  }
}

export function resolvePreviewSiteUrl(
  target: PageMetafieldsPreviewTarget,
  config: Pick<AdminPagesEditorConfig, 'publicSiteUrl' | 'betaSiteUrl'>,
): string {
  if (target === 'beta' && config.betaSiteUrl) return config.betaSiteUrl;
  return config.publicSiteUrl;
}

function safeOrigin(value: string): string | null {
  try {
    const url = new URL(value);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
    return url.origin;
  } catch {
    return null;
  }
}

/**
 * The beta target is only offered when it points somewhere different from the
 * live site (e.g. the production dashboard already previews against beta).
 */
export function getPreviewTargetOptions(
  config: Pick<AdminPagesEditorConfig, 'publicSiteUrl' | 'betaSiteUrl'>,
): PageMetafieldsPreviewTargetOption[] {
  const liveOrigin = safeOrigin(config.publicSiteUrl);
  const betaOrigin = safeOrigin(config.betaSiteUrl);
  if (!betaOrigin || betaOrigin === liveOrigin) {
    return [{ label: 'Live', value: 'live' }];
  }
  return [
    { label: 'Live', value: 'live' },
    { label: 'Beta', value: 'beta' },
  ];
}

export function normalizePagePath(value: string, siteUrl: string): string {
  const trimmed = value.trim();
  if (!trimmed) return '/';
  try {
    return new URL(trimmed, siteUrl).pathname || '/';
  } catch {
    return trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
  }
}
